import React, { useCallback, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import Dashboard from "./pages/Dashboard/Dashboard";
import NotFound from "./pages/NotFound";
import { useTheme } from "./contexts/ThemeContext";

import AlertDialog from "./components/AlertDialog/AlertDialog";
import Home from "./pages/Home/Home";
import {
    deleteStorage,
    getStorage,
    hasAccess,
    httpRequest,
    isRoot,
    isSubstringInArray,
    onRequestError,
    setStorage,
} from "./helper/global";
import { LOCAL_STORAGE_KEY, PERMISSIONS } from "./helper/constant";
import { useDispatch, useSelector } from "react-redux";
import { resetUserProfile, setUserProfile } from "./reducers/userSlice";

import Patients from "./pages/Patients/Patients";
import Patient from "./pages/Patient/Patient";
import Loader from "./components/Loader/Loader";
import Users from "./pages/Users/Users";
import { setPermissionConfig } from "./reducers/permissionSlice";
import Emails from "./pages/Emails/Emails";
import Education from "./pages/Education/Education";
import IdleTimer from "./components/IdleTimer/IdleTimer";

export default function AppRoutes() {
    const { theme } = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const user = useSelector((state) => state.user.profile);
    const permissionConfig = useSelector((state) => state.permission);
    const loader = useSelector((state) => state.loader);

    const validateSessionToken = useCallback(async () => {
        const result = await httpRequest(
            "user/validate",
            {},
            "GET",
            (error) => {
                deleteStorage(LOCAL_STORAGE_KEY.token);
                dispatch(resetUserProfile());

                //if root path, don't show alert dialog
                if (isRoot(location)) {
                    return;
                }

                onRequestError(dispatch, navigate, {
                    title: error.title,
                    message: error.message,
                });
            },
        );

        if (result === false) {
            return;
        }

        //store jwt token in local storage
        setStorage(LOCAL_STORAGE_KEY.token, result.payload.sessionToken);

        dispatch(
            setUserProfile({
                profile: result.payload.user,
                sessionToken: result.payload.sessionToken,
            }),
        );

        dispatch(setPermissionConfig(result.payload.permissionConfig));

        if (isRoot(location)) {
            navigate("/patients");
        }
    }, [location]);

    //check every page load if sessionToken is still valid
    useEffect(() => {
        if (isRoot(location) && user) {
            navigate("/patients");
        }

        const routes = [
            "home",
            "patient",
            "patients",
            "account",
            "user",
            "users",
            "emails",
            "education",
        ];

        if (
            !isRoot(location) &&
            !isSubstringInArray(location.pathname, routes)
        ) {
            return;
        }

        if (user) {
            return;
        }

        const jwtToken = getStorage(LOCAL_STORAGE_KEY.token);

        if (!jwtToken && isRoot(location)) {
            return;
        }

        validateSessionToken();
    }, [location]);

    return (
        <>
            <Loader loader={loader} />
            <Routes>
                <Route exact path="/" element={<Home theme={theme} />} />

                {hasAccess(
                    user,
                    permissionConfig,
                    "email",
                    PERMISSIONS.read,
                ) && (
                    <Route
                        exact
                        path="/emails"
                        element={
                            <Emails
                                theme={theme}
                                user={user}
                                permissionConfig={permissionConfig}
                            />
                        }
                    />
                )}

                {hasAccess(
                    user,
                    permissionConfig,
                    "education",
                    PERMISSIONS.write,
                ) && (
                    <Route
                        exact
                        path="/education"
                        element={
                            <Education
                                theme={theme}
                                user={user}
                                permissionConfig={permissionConfig}
                            />
                        }
                    />
                )}

                <Route
                    exact
                    path="/home"
                    element={
                        <Dashboard
                            theme={theme}
                            user={user}
                            permissionConfig={permissionConfig}
                        />
                    }
                />

                <Route
                    exact
                    path="/patient/id/:id"
                    element={
                        <Patient
                            theme={theme}
                            user={user}
                            permissionConfig={permissionConfig}
                        />
                    }
                />

                <Route
                    exact
                    path="/patients"
                    element={
                        <Patients
                            theme={theme}
                            user={user}
                            permissionConfig={permissionConfig}
                        />
                    }
                />

                <Route
                    exact
                    path="/user/:id"
                    element={
                        <Patient
                            theme={theme}
                            user={user}
                            permissionConfig={permissionConfig}
                        />
                    }
                />

                <Route
                    exact
                    path="/users"
                    element={
                        <Users
                            theme={theme}
                            user={user}
                            permissionConfig={permissionConfig}
                        />
                    }
                />

                <Route path="*" element={<NotFound theme={theme} />} />
            </Routes>

            <AlertDialog theme={theme} />
            <IdleTimer isAuthenticated={!!user} />
        </>
    );
}
